import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, today } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Building2, TrendingDown, Wand2, AlertTriangle, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

const METHODS = [
    { v: "SLM", label: "Straight-Line (SLM)" },
    { v: "WDV", label: "Written-Down Value (WDV)" },
    { v: "DD", label: "Double-Declining" },
];

const CATEGORIES = ["Computer Equipment", "Office Equipment", "Furniture & Fixtures", "Vehicles", "Plant & Machinery", "Buildings", "Land", "Software", "Other"];

export default function FixedAssets() {
    const [assets, setAssets] = useState([]);
    const [summary, setSummary] = useState(null);
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const [scheduleFor, setScheduleFor] = useState(null);
    const [scheduleData, setScheduleData] = useState(null);
    const [postOpen, setPostOpen] = useState(false);
    const [postForm, setPostForm] = useState({ period_from: today().slice(0, 7) + "-01", period_to: today() });

    const empty = { name: "", code: "", category: "Computer Equipment", purchase_date: today(), cost: 0, salvage_value: 0, useful_life_years: 5, method: "SLM", location: "", notes: "" };
    const [form, setForm] = useState(empty);

    const load = async () => {
        const [a, s] = await Promise.all([api.get("/assets"), api.get("/assets/summary")]);
        setAssets(a.data); setSummary(s.data);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, cost: Number(form.cost), salvage_value: Number(form.salvage_value), useful_life_years: Number(form.useful_life_years) };
            if (drawer.item) await api.put(`/assets/${drawer.item.id}`, payload);
            else await api.post("/assets", payload);
            toast.success(drawer.item ? "Asset updated" : "Asset added");
            setDrawer({ open: false, item: null });
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const openSchedule = async (asset) => {
        setScheduleFor(asset); setScheduleData(null);
        const { data } = await api.get(`/assets/${asset.id}/depreciation-schedule`);
        setScheduleData(data);
    };

    const postDepreciation = async () => {
        try {
            const { data } = await api.post("/assets/post-depreciation", postForm);
            if (data.posted) {
                toast.success(`Posted ${fmtMoney(data.total_depreciation)} across ${data.detail.length} assets`);
            } else {
                toast.info(data.message);
            }
            setPostOpen(false);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Posting failed");
        }
    };

    const deleteAsset = async (id) => {
        if (!confirm("Delete this asset? This cannot be undone.")) return;
        await api.delete(`/assets/${id}`);
        toast.success("Asset deleted");
        await load();
    };

    const openEdit = (item) => {
        setForm(item ? { ...empty, ...item } : empty);
        setDrawer({ open: true, item });
    };

    return (
        <>
            <PageHeader
                kicker="INVENTORY • FIXED ASSETS"
                title="Fixed Asset Register"
                subtitle="Track tangible & intangible assets with SLM/WDV/Double-Declining depreciation and period-end auto-posting."
                actions={
                    <>
                        <Button variant="outline" onClick={() => setPostOpen(true)} data-testid="post-depreciation-btn"><Wand2 className="w-4 h-4 mr-1.5" />Post depreciation</Button>
                        <Button onClick={() => openEdit(null)} data-testid="asset-new"><Plus className="w-4 h-4 mr-1.5" />New asset</Button>
                    </>
                }
            />
            <PageBody>
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <Card label="Total assets" value={summary.count} />
                        <Card label="Original cost" value={fmtMoney(summary.total_cost)} accent="muted" />
                        <Card label="Accumulated dep." value={fmtMoney(summary.total_accumulated_depreciation)} accent="danger" />
                        <Card label="Net book value" value={fmtMoney(summary.total_book_value)} accent="positive" />
                    </div>
                )}
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {assets.length === 0 ? (
                        <div className="p-10 text-center text-muted-foreground">
                            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <div className="text-sm">No fixed assets yet. Add your first asset to start tracking depreciation.</div>
                        </div>
                    ) : (
                        <table className="fin-table">
                            <thead><tr><th>Code</th><th>Name</th><th>Category</th><th>Method</th><th>Purchased</th><th className="num">Cost</th><th className="num">Book value</th><th></th></tr></thead>
                            <tbody>
                                {assets.map((a) => (
                                    <tr key={a.id} data-testid={`asset-row-${a.id}`}>
                                        <td className="font-mono text-[12px]">{a.code}</td>
                                        <td>
                                            <div className="font-medium">{a.name}</div>
                                            <div className="text-[11.5px] text-muted-foreground">{a.location || "—"}</div>
                                        </td>
                                        <td className="text-[12.5px]">{a.category || "—"}</td>
                                        <td><span className="text-[10.5px] font-semibold px-2 py-0.5 rounded bg-[#a68a56]/12 text-[#a68a56]">{a.method || "SLM"}</span></td>
                                        <td className="text-[12px] text-muted-foreground">{fmtDate(a.purchase_date)}</td>
                                        <td className="num">{fmtMoney(a.cost)}</td>
                                        <td className="num font-medium">{fmtMoney(a.current_book_value)}</td>
                                        <td className="text-right whitespace-nowrap">
                                            <Button size="sm" variant="ghost" onClick={() => openSchedule(a)} data-testid={`asset-schedule-${a.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                                            <Button size="sm" variant="ghost" onClick={() => openEdit(a)}>Edit</Button>
                                            <Button size="sm" variant="ghost" onClick={() => deleteAsset(a.id)}><Trash2 className="w-3.5 h-3.5 text-[#b4233a]" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </PageBody>

            {/* New/Edit asset drawer */}
            <Sheet open={drawer.open} onOpenChange={(v) => !v && setDrawer({ open: false, item: null })}>
                <SheetContent side="right" className="w-full sm:max-w-[600px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4"><SheetTitle className="text-xl font-semibold tracking-tight">{drawer.item ? "Edit asset" : "New fixed asset"}</SheetTitle></SheetHeader>
                    <form className="flex-1 overflow-y-auto p-6 space-y-3" onSubmit={save}>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" data-testid="asset-name" /></div>
                            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="auto" className="mt-1" /></div>
                            <div>
                                <Label>Category</Label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Purchase date</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="mt-1" /></div>
                            <div><Label>Cost</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="mt-1" data-testid="asset-cost" /></div>
                            <div><Label>Salvage value</Label><Input type="number" value={form.salvage_value} onChange={(e) => setForm({ ...form, salvage_value: e.target.value })} className="mt-1" /></div>
                            <div><Label>Useful life (years)</Label><Input type="number" value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })} className="mt-1" /></div>
                            <div className="col-span-2">
                                <Label>Depreciation method</Label>
                                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>{METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Mumbai HQ, Floor 3, Bay B12" className="mt-1" /></div>
                            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
                        </div>
                        <Button type="submit" className="w-full" data-testid="asset-save">{drawer.item ? "Update asset" : "Add asset"}</Button>
                    </form>
                </SheetContent>
            </Sheet>

            {/* Schedule dialog */}
            <Dialog open={!!scheduleFor} onOpenChange={(v) => !v && setScheduleFor(null)}>
                <DialogContent className="max-w-3xl" data-testid="asset-schedule-dialog">
                    <DialogHeader>
                        <div className="text-[10px] font-semibold tracking-[0.18em] text-[#a68a56]">DEPRECIATION SCHEDULE</div>
                        <DialogTitle className="text-xl">{scheduleFor?.name}</DialogTitle>
                        <p className="text-[12.5px] text-muted-foreground">{scheduleFor?.method} · {scheduleFor?.useful_life_years} years · Cost {fmtMoney(scheduleFor?.cost)} · Salvage {fmtMoney(scheduleFor?.salvage_value)}</p>
                    </DialogHeader>
                    {!scheduleData ? (
                        <div className="p-8 text-center text-muted-foreground">Loading…</div>
                    ) : (
                        <div className="border border-border rounded-md overflow-hidden">
                            <table className="fin-table text-[12.5px]">
                                <thead><tr><th>Year</th><th>Year-end</th><th className="num">Opening</th><th className="num">Depreciation</th><th className="num">Accumulated</th><th className="num">Closing book</th></tr></thead>
                                <tbody>
                                    {scheduleData.schedule.map((r) => (
                                        <tr key={r.year}>
                                            <td className="font-mono">Y{r.year}</td>
                                            <td className="text-[11.5px] text-muted-foreground">{fmtDate(r.year_end)}</td>
                                            <td className="num">{fmtMoney(r.opening_value)}</td>
                                            <td className="num text-[#b4233a]">{fmtMoney(r.depreciation)}</td>
                                            <td className="num text-muted-foreground">{fmtMoney(r.accumulated)}</td>
                                            <td className="num font-medium">{fmtMoney(r.closing_value)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Post depreciation dialog */}
            <Dialog open={postOpen} onOpenChange={setPostOpen}>
                <DialogContent className="max-w-md" data-testid="post-dep-dialog">
                    <DialogHeader>
                        <div className="text-[10px] font-semibold tracking-[0.18em] text-[#a68a56]">PERIOD-END POSTING</div>
                        <DialogTitle className="text-xl">Post depreciation journal</DialogTitle>
                        <p className="text-[12.5px] text-muted-foreground">Calculates pro-rata depreciation across all active assets and posts a single Dr Expense / Cr Accumulated Depreciation journal.</p>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>From</Label><Input type="date" value={postForm.period_from} onChange={(e) => setPostForm({ ...postForm, period_from: e.target.value })} className="mt-1" /></div>
                            <div><Label>To</Label><Input type="date" value={postForm.period_to} onChange={(e) => setPostForm({ ...postForm, period_to: e.target.value })} className="mt-1" /></div>
                        </div>
                        <div className="bg-[#fef3c7] border border-[#fbbf24]/40 rounded-md p-3 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-[#c97a3a] mt-0.5 shrink-0" />
                            <p className="text-[11.5px] text-[#92400e]">Make sure your CoA has codes <b>6950</b> (Depreciation Expense) and <b>1600</b> (Accumulated Depreciation). Already-depreciated periods will not double-count.</p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" className="flex-1" onClick={() => setPostOpen(false)}>Cancel</Button>
                            <Button className="flex-1" onClick={postDepreciation} data-testid="post-dep-confirm"><TrendingDown className="w-4 h-4 mr-1.5" />Post</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

function Card({ label, value, accent }) {
    const color = accent === "positive" ? "text-[#1f6c47]" : accent === "danger" ? "text-[#b4233a]" : accent === "muted" ? "text-muted-foreground" : "text-foreground";
    return (
        <div className="border border-border rounded-md p-4 bg-card">
            <div className="text-[10.5px] tracking-[0.14em] font-semibold text-muted-foreground">{label.toUpperCase()}</div>
            <div className={`text-xl font-semibold mt-1 ${color}`}>{value}</div>
        </div>
    );
}
